import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/shared/PageHeader";
import { DataTable } from "@/components/shared/DataTable";
import { StatusPill } from "@/components/shared/StatusPill";
import { toast } from "sonner";

function ProviderAccess() {
  const [policy, setPolicy] = useState(null);
  const [exempt, setExempt] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [requests, setRequests] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/provider-access").then((r) => { setPolicy(r.data.policy); setExempt(r.data.exempt); }).catch(() => setPolicy(undefined));
    api.get("/admin/provider-sessions").then((r) => setSessions(r.data)).catch(() => {});
    api.get("/admin/provider-requests").then((r) => setRequests(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const setP = async (p) => { try { await api.put("/admin/provider-access", { policy: p }); setPolicy(p); toast.success("Provider access updated"); } catch (e) { toast.error(apiError(e)); } };
  const approve = async (id) => { try { await api.post(`/admin/provider-requests/${id}/approve`); toast.success("Access approved for 24 hours"); load(); } catch (e) { toast.error(apiError(e)); } };

  const pending = requests.filter((r) => r.status === "pending");
  return (
    <div className="space-y-6 mb-8" data-testid="provider-access-section">
      {policy !== undefined && (
        <div className="panel p-5">
          <h3 className="font-heading font-semibold text-slate-900 mb-1">Provider access</h3>
          <p className="text-sm text-slate-500 mb-3">Controls how Total Operations support may enter this company.</p>
          {exempt ? <p className="text-sm text-slate-500">This is the Total Operations workspace and is exempt from this setting.</p> : (
            <div className="inline-flex rounded-lg border p-0.5" style={{ borderColor: "var(--line)" }} data-testid="provider-policy-toggle">
              {[["ask", "Ask each time"], ["standing", "Standing access"]].map(([v, l]) => (
                <button key={v} onClick={() => setP(v)} className={`px-3 py-1.5 text-sm rounded-md ${policy === v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid={`provider-policy-${v}`}>{l}</button>))}
            </div>)}
          {pending.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="provider-requests">
              <p className="eyebrow">Pending access requests</p>
              {pending.map((r) => (
                <div key={r.id} className="flex items-center justify-between text-sm border rounded-lg px-3 py-2" style={{ borderColor: "var(--line)" }}>
                  <span>{r.support_user_name} {r.reason ? `(${r.reason})` : ""}</span>
                  <button onClick={() => approve(r.id)} className="px-3 py-1 rounded-md bg-emerald-600 text-white text-xs" data-testid={`provider-approve-${r.id}`}>Approve 24h</button>
                </div>))}
            </div>)}
        </div>)}
      <div className="panel overflow-hidden" data-testid="provider-sessions-log">
        <div className="px-4 py-2.5 border-b" style={{ borderColor: "var(--line)" }}><span className="text-sm font-semibold text-slate-900">Provider sessions</span></div>
        <DataTable testid="provider-sessions-table" columns={[
          { key: "support_user_name", header: "Provider", render: (r) => r.support_user_name || r.support_user_id },
          { key: "started_at", header: "Entered", render: (r) => new Date(r.started_at).toLocaleString() },
          { key: "ended_at", header: "Left", render: (r) => r.ended_at ? new Date(r.ended_at).toLocaleString() : <StatusPill value="active" tone="warning" /> },
          { key: "reason", header: "Reason", render: (r) => <span className="text-xs text-slate-500">{r.reason || ""}</span> },
        ]} rows={sessions} empty="No provider sessions" />
      </div>
    </div>);
}

export default function Audit() {
  const [events, setEvents] = useState([]);
  useEffect(() => { api.get("/admin/audit").then((r) => setEvents(r.data)).catch(() => {}); }, []);
  const cols = [
    { key: "created_at", header: "When", render: (r) => new Date(r.created_at).toLocaleString() },
    { key: "event", header: "Event", render: (r) => <span className="font-mono text-xs text-slate-700">{r.event}</span> },
    { key: "actor_name", header: "Actor", render: (r) => r.actor_name || "System" },
    { key: "support", header: "Source", render: (r) => r.support ? <StatusPill value="provider" tone="warning" /> : <span className="text-slate-400 text-xs">normal</span> },
    { key: "target", header: "Target", render: (r) => <span className="text-xs text-slate-500">{r.target && Object.keys(r.target).length ? JSON.stringify(r.target) : ""}</span> },
  ];
  return (
    <div>
      <PageHeader title="Audit" subtitle="Append-only security, access & administrative evidence" />
      <ProviderAccess />
      <DataTable testid="admin-audit-table" columns={cols} rows={events} empty="No audit events" />
    </div>
  );
}
